<template>
  <div class="pay-shell">
    <transition name="toast">
      <div v-if="toast.show" class="toast" :class="'toast--' + toast.type" role="status" aria-live="polite">
        <svg class="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <path v-if="toast.type === 'success'" d="M20 6 9 17l-5-5" />
          <template v-else-if="toast.type === 'error'">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </template>
          <template v-else-if="toast.type === 'warning'">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </template>
          <template v-else>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </template>
        </svg>
        <span class="toast-message">{{ toast.message }}</span>
        <button class="toast-close" @click="closeToast" aria-label="关闭提示">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        </button>
      </div>
    </transition>

    <!-- 第一步：输入充值金额 -->
    <div v-if="stage === 'input'" class="pay-card">
      <header class="card-header">
        <div class="brand">
          <span class="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg>
          </span>
          <div>
            <h1>账户充值</h1>
            <p>资金实时到账，充值时请认准支付宝官方收银台</p>
          </div>
        </div>
        <div class="secure-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1Z" /><path d="m9 12 2 2 4-4" /></svg>
          <span>安全支付</span>
        </div>
      </header>

      <div class="input-layout">
        <section class="balance-panel">
          <span class="panel-eyebrow">当前账户余额</span>
          <div class="balance-value">
            <span class="balance-currency">¥</span>
            <span class="balance-number">{{ balance }}</span>
          </div>
          <p class="balance-note">充值成功后，余额会自动更新</p>
          <div class="balance-card-footer">
            <span class="status-pip"></span>
            <span>账户状态正常</span>
          </div>
        </section>

        <section class="form-panel">
          <label class="field-label" for="recharge-amount">充值金额</label>
          <div class="amount-input-shell" :class="{ 'has-error': amountError }">
            <span class="amount-currency">¥</span>
            <input
              id="recharge-amount"
              v-model.number="selectedOption"
              type="number"
              inputmode="decimal"
              min="1"
              max="50"
              step="0.01"
              placeholder="0.00"
              class="amount-input"
              @input="validateAmount"
            />
          </div>

          <div class="quick-amounts" aria-label="快捷金额">
            <button
              v-for="amount in quickAmounts"
              :key="amount"
              type="button"
              class="quick-chip"
              :class="{ active: selectedOption === amount }"
              @click="selectAmount(amount)"
            >
              ¥{{ amount }}
            </button>
          </div>

          <div class="amount-hint" :class="{ 'hint-error': amountError }">
            <span v-if="!amountError">单笔充值范围 ¥1 — ¥50</span>
            <span v-else>{{ amountErrorMessage }}</span>
          </div>

          <div class="method-section">
            <span class="field-label">支付方式</span>
            <div class="method-item">
              <span class="method-icon" aria-hidden="true">
                <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                  <path d="M230.771014 576.556522c-12.614493 9.646377-25.228986 23.744928-28.93913 42.295652-5.194203 24.486957-0.742029 55.652174 22.26087 80.13913 28.93913 28.93913 72.718841 37.101449 92.011594 38.585508 51.2 3.710145 106.110145-22.26087 147.663768-50.457971 16.324638-11.130435 43.77971-34.133333 70.492754-69.750725-59.362319-30.423188-133.565217-64.556522-212.22029-61.588406-41.553623 1.484058-70.492754 9.646377-91.269566 20.776812zM983.188406 712.347826c25.971014-61.588406 40.811594-129.113043 40.811594-200.347826 0-281.971014-230.028986-512-512-512S0 230.028986 0 512s230.028986 512 512 512c170.666667 0 321.298551-83.849275 414.794203-212.22029C838.492754 768.742029 693.797101 696.023188 604.011594 652.985507c-42.295652 48.973913-105.368116 97.205797-176.602898 117.982609-44.521739 13.356522-85.333333 18.550725-126.886957 9.646377-42.295652-8.904348-72.718841-28.197101-90.527536-47.489855-8.904348-10.388406-19.292754-22.26087-27.455073-37.843479 0.742029 0.742029 0.742029 2.226087 0.742029 2.968116 0 0-4.452174-7.42029-7.420289-19.292753-1.484058-5.936232-2.968116-11.872464-3.710145-17.808696-0.742029-4.452174-0.742029-8.904348 0-12.614493-0.742029-7.42029 0-15.582609 1.484058-23.744927 4.452174-20.776812 12.614493-43.77971 35.617391-65.298551 48.973913-48.231884 115.014493-50.457971 149.147826-50.457971 50.457971 0.742029 138.017391 22.26087 212.22029 48.973913 20.776812-43.77971 34.133333-89.785507 42.295652-121.692754H304.973913v-33.391304h158.052174v-66.782609H272.324638v-34.133333h190.701449v-66.782609c0-8.904348 2.226087-16.324638 16.324638-16.324637h74.944927v83.107246h207.026087v33.391304H554.295652v66.782609H719.768116S702.701449 494.933333 651.501449 586.202899c115.014493 40.811594 277.518841 104.626087 331.686957 126.144927z m0 0" fill="#06B4FD"/>
                </svg>
              </span>
              <span class="method-name">支付宝支付</span>
              <span class="method-check" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              </span>
            </div>
          </div>
        </section>
      </div>

      <footer class="card-footer">
        <button
          class="btn-primary"
          type="button"
          :disabled="isCreatingOrder || amountError || !selectedOption"
          :class="{ 'btn-loading': isCreatingOrder }"
          @click="createPayment"
        >
          <span v-if="!isCreatingOrder">立即充值</span>
          <span v-else class="loading-text">
            <span class="spinner"></span>
            正在创建订单...
          </span>
        </button>
      </footer>
    </div>

    <!-- 第二步：扫码支付 -->
    <div v-else-if="stage === 'payment'" class="pay-card">
      <header class="card-header compact">
        <div class="steps" aria-label="支付进度">
          <span class="step done">1 填写金额</span>
          <span class="step active">2 扫码支付</span>
          <span class="step">3 完成</span>
        </div>
        <span class="timer-chip" :class="{ warning: countdownSeconds <= 10 }">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
          {{ countdown }}
        </span>
      </header>

      <div v-if="!isTimeout" class="payment-layout">
        <section class="order-panel">
          <span class="panel-eyebrow">本次交易金额</span>
          <div class="order-amount">
            <span class="order-currency">¥</span>
            <span class="order-number">{{ paymentAmount }}</span>
          </div>

          <div class="order-meta">
            <div class="meta-row">
              <span>订单号</span>
              <strong>{{ orderId || '创建中...' }}</strong>
            </div>
            <div class="meta-row">
              <span>支付方式</span>
              <strong>{{ paymentMethod }}</strong>
            </div>
          </div>

          <div class="paying-state" :class="pollingStatus">
            <span class="status-pip"></span>
            <span>{{ statusTextMap[pollingStatus] }}</span>
          </div>
        </section>

        <section class="qr-panel">
          <div class="qr-frame">
            <span class="corner corner--tl"></span>
            <span class="corner corner--tr"></span>
            <span class="corner corner--bl"></span>
            <span class="corner corner--br"></span>
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="支付宝收款二维码" class="qr-code" />
            <div v-else class="qr-loading">
              <span class="spinner spinner--dark"></span>
              <span>二维码生成中...</span>
            </div>
          </div>
          <p class="qr-hint">请使用支付宝扫码完成支付</p>
          <div class="polling-hint">
            <span class="spinner-small"></span>
            系统正在自动确认到账结果
          </div>
        </section>
      </div>

      <div v-else class="timeout-panel">
        <div class="result-icon icon-warning">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
        </div>
        <h2>支付时间已结束</h2>
        <p>订单未在有效期内完成支付，可刷新查询最新状态，或重新生成二维码继续支付。</p>
        <div class="timeout-actions">
          <button class="btn-secondary" type="button" :disabled="isCheckingStatus" @click="checkPaymentStatusManually">
            <span v-if="!isCheckingStatus">刷新支付状态</span>
            <span v-else class="loading-text"><span class="spinner"></span>检测中...</span>
          </button>
          <button class="btn-primary" type="button" :disabled="isRegenerating" @click="regenerateQRCode">
            <span v-if="!isRegenerating">重新生成二维码</span>
            <span v-else class="loading-text"><span class="spinner"></span>生成中...</span>
          </button>
        </div>
      </div>

      <footer class="card-footer split">
        <button class="btn-ghost" type="button" @click="handleBack">返回上一步</button>
        <button
          v-if="!isTimeout"
          class="btn-primary flex-auto"
          type="button"
          :disabled="isConfirmingPayment"
          :class="{ 'btn-loading': isConfirmingPayment }"
          @click="handlePaymentComplete"
        >
          <span v-if="!isConfirmingPayment">我已支付完成</span>
          <span v-else class="loading-text">
            <span class="spinner"></span>
            正在验证...
          </span>
        </button>
      </footer>
    </div>

    <!-- 结果页：成功 / 失败 -->
    <div v-else class="pay-card result-card">
      <div class="result-icon" :class="stage === 'success' ? 'icon-success' : 'icon-danger'">
        <svg v-if="stage === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
      </div>

      <template v-if="stage === 'success'">
        <h2>支付成功</h2>
        <p class="result-desc">充值金额已到账，当前余额为 <strong>¥{{ balance }}</strong></p>
        <div class="result-actions">
          <button class="btn-primary" type="button" @click="goToHome">返回首页</button>
          <button class="btn-ghost" type="button" @click="goToRechargeRecords">查看充值记录</button>
        </div>
      </template>
      <template v-else>
        <h2>支付失败</h2>
        <p class="result-desc">未完成支付或支付未成功，请重新发起订单。如有疑问请联系客服：19872424497</p>
        <div class="result-actions">
          <button class="btn-primary" type="button" @click="retryPayment">重新支付</button>
          <button class="btn-ghost" type="button" @click="handleBack">返回修改金额</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { createOrder, checkOrderStatus } from '@/api/pay'
import { getUserInfo } from '@/api/upfile'
import QRCode from 'qrcode'
import { mapGetters } from 'vuex'

export default {
  name: 'PayRecharge',
  data () {
    return {
      stage: 'input',
      selectedOption: null,
      paymentMethod: '支付宝支付',
      quickAmounts: [1, 5, 10, 20, 50],
      paymentAmount: null,
      orderId: null,
      qrCodeUrl: null,
      countdown: '5:00',
      countdownSeconds: 300,
      countdownTimer: null,
      pollingTimer: null,
      pollingInterval: 3000,
      maxPollingAttempts: 100,
      pollingAttempts: 0,
      pollingStatus: 'pending',
      lastPollResult: null,
      isTimeout: false,
      isLoading: true,
      isCreatingOrder: false,
      isConfirmingPayment: false,
      isCheckingStatus: false,
      isRegenerating: false,
      amountError: false,
      amountErrorMessage: '',
      toast: {
        show: false,
        message: '',
        type: 'info'
      },
      toastTimer: null
    }
  },
  computed: {
    ...mapGetters(['balance']),
    statusTextMap () {
      return {
        pending: '等待支付宝到账',
        success: '支付已到账',
        failed: '支付未成功'
      }
    }
  },
  async created () {
    await this.fetchUserInfo()
  },
  beforeDestroy () {
    this.clearAllTimers()
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
  },
  methods: {
    showToast (message, type = 'info', duration = 3000) {
      this.toast.message = message
      this.toast.type = type
      this.toast.show = true
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toast.show = false
      }, duration)
    },
    closeToast () {
      this.toast.show = false
      if (this.toastTimer) clearTimeout(this.toastTimer)
    },
    selectAmount (amount) {
      this.selectedOption = amount
      this.validateAmount()
    },
    validateAmount () {
      if (this.selectedOption === null || this.selectedOption === '') {
        this.amountError = false
        this.amountErrorMessage = ''
        return
      }

      if (this.selectedOption < 1) {
        this.amountError = true
        this.amountErrorMessage = '充值金额不能低于 ¥1'
      } else if (this.selectedOption > 50) {
        this.amountError = true
        this.amountErrorMessage = '充值金额不能超过 ¥50'
      } else {
        this.amountError = false
        this.amountErrorMessage = ''
      }
    },
    async fetchUserInfo () {
      try {
        this.isLoading = true
        const response = await getUserInfo()
        if (response.status === 200) {
          const balance = response.data && response.data.account_balance
          this.$store.commit('setBalance', parseFloat(balance || 0).toFixed(2))
        } else {
          this.showToast(response.message || '获取用户信息失败', 'error')
        }
      } catch (error) {
        if (error.message && error.message.includes('No token')) {
          return
        }
        this.showToast('获取用户信息失败，请刷新后重试', 'error')
      } finally {
        this.isLoading = false
      }
    },
    startCountdown () {
      this.countdownSeconds = 300
      this.updateCountdownDisplay()

      this.countdownTimer = setInterval(() => {
        if (this.countdownSeconds > 0) {
          this.countdownSeconds -= 1
          this.updateCountdownDisplay()
          return
        }

        this.clearCountdown()
        this.isTimeout = true
        this.checkPaymentStatus(true)
      }, 1000)

      this.startPolling()
    },
    updateCountdownDisplay () {
      const minutes = Math.floor(this.countdownSeconds / 60)
      const seconds = this.countdownSeconds % 60
      this.countdown = `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    startPolling () {
      this.pollingAttempts = 0
      this.pollingStatus = 'pending'
      this.checkPaymentStatus()

      this.pollingTimer = setInterval(() => {
        const attemptsExceeded = this.isTimeout
          ? this.pollingAttempts >= 3
          : this.pollingAttempts >= this.maxPollingAttempts

        if (attemptsExceeded) {
          this.clearPolling()
          if (this.pollingStatus !== 'success') {
            this.pollingStatus = 'failed'
            this.lastPollResult = 'timeout'
          }
          return
        }

        this.checkPaymentStatus()
      }, this.pollingInterval)
    },
    clearCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    clearPolling () {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    clearAllTimers () {
      this.clearCountdown()
      this.clearPolling()
    },
    async createPayment () {
      if (this.isCreatingOrder) return
      if (!this.selectedOption || this.selectedOption < 1 || this.selectedOption > 50) {
        this.validateAmount()
        this.showToast('请输入有效的充值金额（¥1 — ¥50）', 'warning')
        return
      }

      this.isCreatingOrder = true
      try {
        const response = await createOrder({
          amount: this.selectedOption,
          paymentMethod: this.paymentMethod
        })

        if (response.status === 200) {
          this.orderId = response.orderId
          this.paymentAmount = this.selectedOption
          this.isTimeout = false
          this.pollingStatus = 'pending'
          this.lastPollResult = null

          if (response.qrCodeLink) {
            this.qrCodeUrl = await QRCode.toDataURL(response.qrCodeLink, {
              width: 320,
              margin: 1,
              color: {
                dark: '#0b1b3a',
                light: '#ffffff'
              }
            })
          }

          this.stage = 'payment'
          this.startCountdown()
          this.showToast('订单创建成功，请扫码支付', 'success')
        } else {
          this.showToast(response.message || '创建订单失败，请稍后重试', 'error')
        }
      } catch (error) {
        this.showToast('创建订单失败，请检查网络后重试', 'error')
      } finally {
        this.isCreatingOrder = false
      }
    },
    async checkPaymentStatus (isFinalCheck = false) {
      if (!this.orderId) return

      this.pollingAttempts += 1

      try {
        const response = await checkOrderStatus(this.orderId)
        const orderStatus = response.message

        if (orderStatus === 'PAID') {
          this.clearAllTimers()
          this.pollingStatus = 'success'
          this.lastPollResult = 'success'

          if (response.balance !== undefined && response.balance !== null) {
            this.$store.commit('setBalance', parseFloat(response.balance).toFixed(2))
          } else {
            await this.fetchUserInfo()
          }

          this.showToast('支付成功，余额已更新', 'success')
          setTimeout(() => {
            this.stage = 'success'
          }, 800)
          return
        }

        if (orderStatus === 'FAILED') {
          this.clearAllTimers()
          this.pollingStatus = 'failed'
          this.lastPollResult = 'failed'
          this.showToast('支付未成功，请重新发起订单', 'error')
          setTimeout(() => {
            this.stage = 'failed'
          }, 800)
          return
        }

        this.pollingStatus = 'pending'
        if (isFinalCheck) {
          this.lastPollResult = 'pending'
        }
      } catch (error) {
        if (isFinalCheck) {
          this.pollingStatus = 'failed'
        } else {
          this.pollingStatus = 'pending'
          this.showToast('网络异常，系统将继续尝试确认', 'warning')
        }
      }
    },
    async handlePaymentComplete () {
      if (this.isConfirmingPayment) return
      this.isConfirmingPayment = true

      for (let i = 0; i < 3; i += 1) {
        await this.checkPaymentStatus()
        if (this.pollingStatus === 'success' || this.pollingStatus === 'failed') {
          break
        }
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      this.isConfirmingPayment = false
    },
    async checkPaymentStatusManually () {
      if (this.isCheckingStatus) return
      this.isCheckingStatus = true

      await this.checkPaymentStatus(true)
      if (this.pollingStatus === 'pending') {
        this.showToast('暂未检测到到账记录，请确认支付宝已完成付款', 'info')
      }

      this.isCheckingStatus = false
    },
    async regenerateQRCode () {
      if (this.isRegenerating) return
      this.isRegenerating = true

      this.clearAllTimers()
      this.isTimeout = false
      this.qrCodeUrl = null
      this.orderId = null

      await this.createPayment()
      this.isRegenerating = false
    },
    retryPayment () {
      this.clearAllTimers()
      this.orderId = null
      this.qrCodeUrl = null
      this.pollingStatus = 'pending'
      this.lastPollResult = null
      this.isTimeout = false
      this.createPayment()
    },
    handleBack () {
      this.clearAllTimers()
      this.orderId = null
      this.qrCodeUrl = null
      this.pollingStatus = 'pending'
      this.lastPollResult = null
      this.isTimeout = false
      this.stage = 'input'
    },
    goToHome () {
      this.$router.push('/')
    },
    goToRechargeRecords () {
      this.$router.push('/pay-recode')
    },
    resetPaymentState () {
      this.clearAllTimers()
      this.stage = 'input'
      this.orderId = null
      this.qrCodeUrl = null
      this.pollingStatus = 'pending'
      this.lastPollResult = null
      this.isTimeout = false
    }
  }
}
</script>

<style scoped>
.pay-shell {
  min-height: 100vh;
  padding: 36px 20px 72px;
  color: #14203a;
  background:
    radial-gradient(circle at 8% 0%, rgba(22, 119, 255, 0.12), transparent 30%),
    radial-gradient(circle at 92% 100%, rgba(34, 197, 94, 0.10), transparent 28%),
    linear-gradient(160deg, #eef4ff 0%, #f8faff 46%, #eef3fb 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'PingFang SC', 'Microsoft YaHei', 'Segoe UI', Roboto, sans-serif;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.pay-card {
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(20, 32, 58, 0.06);
  border-radius: 28px;
  box-shadow: 0 24px 60px rgba(24, 59, 110, 0.14);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 28px;
  border-bottom: 1px solid #eef2f7;
}

.card-header.compact {
  padding-top: 20px;
  padding-bottom: 20px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, #1677ff, #0958d9);
  box-shadow: 0 10px 22px rgba(22, 119, 255, 0.24);
}

.brand-mark svg {
  width: 24px;
  height: 24px;
}

.brand h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1.2;
  letter-spacing: -0.3px;
}

.brand p {
  margin: 4px 0 0;
  color: #6b7a99;
  font-size: 13px;
}

.secure-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border-radius: 999px;
  color: #1677ff;
  background: #eaf2ff;
  font-size: 13px;
  font-weight: 600;
}

.secure-badge svg {
  width: 17px;
  height: 17px;
}

.input-layout,
.payment-layout {
  display: grid;
  grid-template-columns: 0.92fr 1.08fr;
}

.balance-panel {
  padding: 34px 30px;
  color: #fff;
  background:
    radial-gradient(circle at 10% 10%, rgba(81, 145, 255, 0.32), transparent 38%),
    linear-gradient(160deg, #12234a 0%, #1c3a78 100%);
}

.panel-eyebrow {
  display: block;
  margin-bottom: 16px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 13px;
  font-weight: 500;
}

.balance-value,
.order-amount {
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-variant-numeric: tabular-nums;
}

.balance-value {
  margin-bottom: 18px;
}

.balance-number {
  font-size: 46px;
  font-weight: 700;
  letter-spacing: -1px;
}

.balance-currency,
.order-currency {
  font-size: 22px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.68);
}

.balance-note {
  margin: 0 0 28px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 13px;
}

.balance-card-footer {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  color: #a9c8ff;
  font-size: 13px;
}

.status-pip {
  display: inline-block;
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 0 4px rgba(52, 211, 153, 0.16);
}

.form-panel {
  padding: 30px;
}

.field-label {
  display: block;
  margin-bottom: 10px;
  color: #31405e;
  font-size: 14px;
  font-weight: 600;
}

.amount-input-shell {
  display: flex;
  align-items: baseline;
  gap: 7px;
  padding: 13px 16px;
  border: 1px solid #dfe6f2;
  border-radius: 16px;
  background: #f8fafd;
  transition: border-color 0.16s, box-shadow 0.16s, background 0.16s;
}

.amount-input-shell:focus-within {
  border-color: #1677ff;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(22, 119, 255, 0.12);
}

.amount-input-shell.has-error {
  border-color: #ef4444;
  background: #fff;
}

.amount-currency {
  color: #6b7a99;
  font-size: 20px;
  font-weight: 600;
}

.amount-input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #14203a;
  font-size: 26px;
  font-weight: 700;
  font-family: inherit;
  font-variant-numeric: tabular-nums;
}

.amount-input::placeholder {
  color: #c1cbd9;
  font-weight: 400;
}

.quick-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 14px;
}

.quick-chip {
  min-width: 58px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dfe6f2;
  border-radius: 10px;
  color: #31405e;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: border-color 0.16s, color 0.16s, background 0.16s, transform 0.08s;
}

.quick-chip:hover {
  border-color: #9fc3ff;
  color: #1677ff;
}

.quick-chip:active {
  transform: scale(0.97);
}

.quick-chip.active {
  border-color: #1677ff;
  color: #1677ff;
  background: #eaf2ff;
}

.amount-hint {
  min-height: 18px;
  margin-top: 9px;
  color: #7d8ba6;
  font-size: 12px;
}

.amount-hint.hint-error {
  color: #ef4444;
  font-weight: 500;
}

.method-section {
  margin-top: 24px;
}

.method-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  border: 1px solid #e4ebf5;
  border-radius: 14px;
  background: #fff;
}

.method-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 10px;
  background: #eef7ff;
}

.method-icon svg {
  width: 23px;
  height: 23px;
}

.method-name {
  flex: 1;
  color: #14203a;
  font-size: 15px;
  font-weight: 600;
}

.method-check {
  display: inline-flex;
  color: #1677ff;
}

.method-check svg {
  width: 18px;
  height: 18px;
}

.card-footer {
  padding: 20px 28px 26px;
  border-top: 1px solid #eef2f7;
}

.card-footer.split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.btn-primary,
.btn-secondary,
.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 0 26px;
  border: 0;
  border-radius: 14px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 650;
  transition: transform 0.08s, background 0.16s, box-shadow 0.16s;
}

.btn-primary {
  width: 100%;
  color: #fff;
  background: linear-gradient(135deg, #1677ff, #0b5cd9);
  box-shadow: 0 12px 24px rgba(22, 119, 255, 0.20);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2a83ff, #0b5cd9);
}

.btn-primary:active:not(:disabled),
.btn-secondary:active:not(:disabled),
.btn-ghost:active {
  transform: scale(0.98);
}

.btn-primary:disabled {
  color: rgba(255, 255, 255, 0.74);
  background: #a7c8ff;
  box-shadow: none;
  cursor: not-allowed;
}

.btn-secondary {
  color: #1677ff;
  background: #eaf2ff;
}

.btn-secondary:hover:not(:disabled) {
  background: #dceaff;
}

.btn-secondary:disabled {
  color: #9aa8bf;
  background: #f0f3f8;
  cursor: not-allowed;
}

.btn-ghost {
  color: #1677ff;
  background: transparent;
  border: 1px solid #d5e5ff;
}

.btn-ghost:hover {
  background: #f4f8ff;
}

.flex-auto {
  flex: 1;
  width: auto;
}

.loading-text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner,
.spinner-small {
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.36);
  border-top-color: #fff;
}

.spinner-small {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(22, 119, 255, 0.18);
  border-top-color: #1677ff;
}

.spinner--dark {
  border-color: rgba(22, 119, 255, 0.20);
  border-top-color: #1677ff;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.steps {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 11px;
  border-radius: 999px;
  color: #8a97ad;
  background: #f1f4f9;
  font-size: 12px;
  font-weight: 600;
}

.step.active {
  color: #1677ff;
  background: #eaf2ff;
}

.step.done {
  color: #1677ff;
  background: #eef7ff;
}

.timer-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border-radius: 999px;
  color: #b45309;
  background: #fff7ed;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.timer-chip svg {
  width: 15px;
  height: 15px;
}

.timer-chip.warning {
  color: #dc2626;
  background: #fef2f2;
}

.order-panel {
  padding: 34px 30px;
  color: #fff;
  background:
    radial-gradient(circle at 12% 14%, rgba(81, 145, 255, 0.28), transparent 40%),
    linear-gradient(160deg, #12234a 0%, #1c3a78 100%);
}

.order-amount {
  margin-bottom: 24px;
}

.order-number {
  font-size: 48px;
  font-weight: 700;
  letter-spacing: -1px;
}

.order-meta {
  display: grid;
  gap: 12px;
  padding: 18px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.meta-row {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  font-size: 13px;
}

.meta-row span {
  color: rgba(255, 255, 255, 0.64);
}

.meta-row strong {
  max-width: 220px;
  overflow: hidden;
  color: #fff;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paying-state {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 24px;
  color: #cbd8f3;
  font-size: 14px;
}

.paying-state .status-pip {
  background: #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.16);
}

.paying-state.success .status-pip {
  background: #34d399;
  box-shadow: 0 0 0 4px rgba(52, 211, 153, 0.16);
}

.paying-state.failed .status-pip {
  background: #f87171;
  box-shadow: 0 0 0 4px rgba(248, 113, 113, 0.16);
}

.qr-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  text-align: center;
}

.qr-frame {
  position: relative;
  width: 258px;
  height: 258px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 1px solid #e5ebf5;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 16px 36px rgba(22, 63, 124, 0.12);
}

.qr-code {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  border-radius: 8px;
}

.qr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #7d8ba6;
  font-size: 13px;
}

.corner {
  position: absolute;
  width: 26px;
  height: 26px;
  border: 3px solid #1677ff;
}

.corner--tl { top: 10px; left: 10px; border-right: 0; border-bottom: 0; border-top-left-radius: 12px; }
.corner--tr { top: 10px; right: 10px; border-left: 0; border-bottom: 0; border-top-right-radius: 12px; }
.corner--bl { bottom: 10px; left: 10px; border-right: 0; border-top: 0; border-bottom-left-radius: 12px; }
.corner--br { right: 10px; bottom: 10px; border-left: 0; border-top: 0; border-bottom-right-radius: 12px; }

.qr-hint {
  margin: 18px 0 12px;
  color: #31405e;
  font-size: 15px;
  font-weight: 600;
}

.polling-hint {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #7d8ba6;
  font-size: 12px;
}

.timeout-panel {
  padding: 46px 30px;
  text-align: center;
}

.timeout-panel h2 {
  margin: 20px 0 8px;
  color: #14203a;
  font-size: 22px;
}

.timeout-panel p {
  max-width: 520px;
  margin: 0 auto 24px;
  color: #6b7a99;
  font-size: 14px;
}

.timeout-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.result-card {
  max-width: 540px;
  padding: 46px 34px;
  text-align: center;
}

.result-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
}

.result-icon svg {
  width: 36px;
  height: 36px;
}

.icon-success {
  color: #16a34a;
  background: #e7f8ef;
}

.icon-danger {
  color: #dc2626;
  background: #feecec;
}

.icon-warning {
  color: #d97706;
  background: #fff4e5;
}

.result-card h2 {
  margin: 22px 0 8px;
  color: #14203a;
  font-size: 24px;
}

.result-desc {
  margin: 0 0 28px;
  color: #6b7a99;
  font-size: 14px;
  line-height: 1.7;
}

.result-desc strong {
  color: #14203a;
  font-variant-numeric: tabular-nums;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.result-actions .btn-primary {
  width: auto;
  min-width: 150px;
}

.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 260px;
  max-width: calc(100vw - 32px);
  padding: 12px 14px 12px 16px;
  border-radius: 16px;
  color: #14203a;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(20, 32, 58, 0.06);
  transform: translateX(-50%);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
}

.toast-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
}

.toast--success .toast-icon { color: #16a34a; }
.toast--error .toast-icon { color: #dc2626; }
.toast--warning .toast-icon { color: #d97706; }
.toast--info .toast-icon { color: #1677ff; }

.toast-message {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
}

.toast-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 50%;
  color: #6b7a99;
  background: rgba(120, 128, 145, 0.14);
  cursor: pointer;
}

.toast-close svg {
  width: 13px;
  height: 13px;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -12px);
}

@media (max-width: 760px) {
  .pay-shell {
    padding: 16px 12px 56px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .brand h1 {
    font-size: 20px;
  }

  .input-layout,
  .payment-layout {
    grid-template-columns: 1fr;
  }

  .balance-panel,
  .form-panel,
  .order-panel,
  .qr-panel {
    padding: 24px 20px;
  }

  .balance-panel {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .order-panel {
    border-bottom: 1px solid #eef2f7;
  }

  .card-footer {
    padding: 18px 20px 22px;
  }

  .card-footer.split,
  .result-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .card-footer.split .btn-ghost,
  .card-footer.split .btn-primary,
  .result-actions .btn-primary,
  .result-actions .btn-ghost {
    width: 100%;
  }

  .toast {
    top: 12px;
    left: 12px;
    right: 12px;
    min-width: 0;
    transform: none;
  }

  .toast-enter,
  .toast-leave-to {
    transform: translateY(-12px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner,
  .spinner-small {
    animation: none;
  }

  .btn-primary:active:not(:disabled),
  .btn-secondary:active:not(:disabled),
  .btn-ghost:active {
    transform: none;
  }
}
</style>
